<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="calendar-input-container"
    ref="calendarContainer"
    :class="{ inline: inline }"
  >
    <div v-if="!inline" class="input-wrapper">
      <input
        :value="formattedDate"
        :placeholder="placeholder"
        class="calendar-input"
        :class="{ 'p-invalid': validation?.$error || modelstate }"
        :disabled="disabled"
        :id="id"
        :aria-describedby="`${id}-help`"
        @click="toggleCalendar"
        @keydown="handleInputKeydown"
        readonly
        ref="input"
      />
      <span
        v-if="showIcon"
        class="calendar-icon"
        @click="toggleCalendar"
        tabindex="0"
        @keydown="handleIconKeydown"
      >
        <i class="cal-icon"></i>
      </span>
    </div>
    <span
      v-if="validation?.$error || modelstate"
      :id="`${id}-help`"
      class="p-error text-left mt-2"
    >
      {{ validation?.$errors[0]?.$message || modelstate }}
    </span>
    <div
      v-if="showCalendar || inline"
      class="calendar-container"
      :style="panelStyle"
      ref="calendarPanel"
    >
      <div class="calendar-header">
        <button
          @click="previousMonth"
          class="nav-button"
          aria-label="Previous month"
          ref="prevButton"
        >
          <i class="cal-lt"></i>
        </button>
        <span class="month-year"
          >{{ monthNames[currentMonth] }} {{ currentYear }}</span
        >
        <button
          @click="nextMonth"
          class="nav-button"
          :disabled="isNextMonthDisabled"
          aria-label="Next month"
          ref="nextButton"
        >
          <i class="cal-gt"></i>
        </button>
      </div>
      <div class="calendar-grid" role="grid">
        <div class="day-header" v-for="day in daysOfWeek" :key="day">
          {{ day }}
        </div>
        <div
          v-for="(day, index) in calendarDays"
          :key="day.date"
          class="day"
          :class="{
            inactive: day.isInactive,
            'selected-start': isSelectedStart(day.date),
            'selected-end': isSelectedEnd(day.date),
            range: isInRange(day.date),
            today: isToday(day.date),
            disabled: isBeforeMinDate(day.date) || isAfterMaxDate(day.date),
          }"
          @click.stop="selectDate(day.date)"
          @keydown="handleDayKeydown($event, index)"
          role="gridcell"
          :aria-label="formatDayAriaLabel(day)"
          :tabindex="isSelectedStart(day.date) || isSelectedEnd(day.date) ? 0 : -1"
        >
          {{ day.day }}
        </div>
      </div>
      <div v-if="showTime" class="time-picker">
        <div class="time-unit">
          <button
            @click="incrementHours"
            class="time-arrow up"
            aria-label="Increment hours"
          >
            <i class="up-cal"></i>
          </button>
          <span
            class="time-value"
            aria-label="Hours"
            tabindex="0"
            @keydown="handleTimeKeydown($event, 'hours')"
            ref="hourInput"
          >
            {{ pad(hours) }}
          </span>
          <button
            @click="decrementHours"
            class="time-arrow down"
            aria-label="Decrement hours"
          >
            <i class="down-cal"></i>
          </button>
        </div>
        <span class="time-separator">:</span>
        <div class="time-unit">
          <button
            @click="incrementMinutes"
            class="time-arrow up"
            aria-label="Increment minutes"
          >
            <i class="up-cal"></i>
          </button>
          <span
            class="time-value"
            aria-label="Minutes"
            tabindex="0"
            @keydown="handleTimeKeydown($event, 'minutes')"
            ref="minuteInput"
          >
            {{ pad(minutes) }}
          </span>
          <button
            @click="decrementMinutes"
            class="time-arrow down"
            aria-label="Decrement minutes"
          >
            <i class="down-cal"></i>
          </button>
        </div>
      </div>
      <div v-if="showButtonBar" class="button-bar">
        <button
          v-if="showClear"
          @click="clearDate"
          class="clear-button"
          aria-label="Clear date"
          ref="clearButton"
        >
          Clear
        </button>
        <button
          @click="selectToday"
          class="today-button"
          aria-label="Select today"
          ref="todayButton"
        >
          Today
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: [Date, String, Array],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Select a date",
    },
    validation: {
      type: Object,
      required: false,
    },
    modelstate: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "calendar-input",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    lang: {
      type: String,
      default: "en",
    },
    showTime: {
      type: Boolean,
      default: false,
    },
    hourFormat: {
      type: String,
      default: "24",
    },
    dateFormat: {
      type: String,
      default: "yy-mm-dd",
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
    },
    maxDate: {
      type: Date,
      default: null,
    },
    selectionMode: {
      type: String,
      default: "single",
      validator: (value) => ["single", "range"].includes(value),
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    showButtonBar: {
      type: Boolean,
      default: false,
    },
    panelStyle: {
      type: Object,
      default: () => ({}),
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const now = new Date();
    let initialRange = [null, null];
    if (this.selectionMode === "single" && this.modelValue) {
      initialRange = [new Date(this.modelValue), null];
    } else if (this.selectionMode === "range" && Array.isArray(this.modelValue)) {
      initialRange = [
        this.modelValue[0] ? new Date(this.modelValue[0]) : null,
        this.modelValue[1] ? new Date(this.modelValue[1]) : null,
      ];
    }
    return {
      showCalendar: false,
      currentMonth: initialRange[0]
        ? initialRange[0].getMonth()
        : now.getMonth(),
      currentYear: initialRange[0]
        ? initialRange[0].getFullYear()
        : now.getFullYear(),
      selectedRange: initialRange,
      hours: initialRange[0] ? initialRange[0].getHours() : now.getHours(),
      minutes: initialRange[0] ? initialRange[0].getMinutes() : now.getMinutes(),
      focusedDayIndex: null,
      monthNames: this.getMonthNames(),
      daysOfWeek: this.getDaysOfWeek(),
    };
  },
  computed: {
    formattedDate() {
      if (this.selectionMode === "single") {
        if (!this.selectedRange[0]) return "";
        const date = this.selectedRange[0];
        let format = this.dateFormat;
        if (this.showTime) {
          format += ` ${this.hourFormat === "24" ? "HH:MM" : "hh:MM A"}`;
        }
        return this.formatDate(date, format);
      } else {
        const start = this.selectedRange[0];
        const end = this.selectedRange[1];
        if (!start) return "";
        let format = this.dateFormat;
        if (this.showTime) {
          format += ` ${this.hourFormat === "24" ? "HH:MM" : "hh:MM A"}`;
        }
        const startFormatted = this.formatDate(start, format);
        const endFormatted = end ? this.formatDate(end, format) : "";
        return end ? `${startFormatted} - ${endFormatted}` : startFormatted;
      }
    },
    calendarDays() {
      const days = [];
      const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
      const lastDayOfMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0
      );
      const startDay = firstDayOfMonth.getDay();
      const totalDays = lastDayOfMonth.getDate();

      // Add previous month's days
      const prevMonthDays = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      for (let i = startDay; i > 0; i--) {
        days.push({
          day: prevMonthDays - i + 1,
          isInactive: true,
          date: new Date(
            this.currentYear,
            this.currentMonth - 1,
            prevMonthDays - i + 1
          ),
        });
      }

      // Add current month's days
      for (let i = 1; i <= totalDays; i++) {
        days.push({
          day: i,
          isInactive: false,
          date: new Date(this.currentYear, this.currentMonth, i),
        });
      }

      // Add next month's days
      const remainingDays = 42 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          day: i,
          isInactive: true,
          date: new Date(this.currentYear, this.currentMonth + 1, i),
        });
      }

      return days;
    },
    isNextMonthDisabled() {
      if (!this.maxDate) return false;
      const nextMonth = new Date(this.currentYear, this.currentMonth + 1, 1);
      const maxDateMonth = new Date(
        this.maxDate.getFullYear(),
        this.maxDate.getMonth(),
        1
      );
      return nextMonth > maxDateMonth;
    },
  },
  methods: {
    pad(num) {
      return String(num).padStart(2, "0");
    },
    getMonthNames() {
      const formatter = new Intl.DateTimeFormat(this.lang, { month: "long" });
      return Array.from({ length: 12 }, (_, i) =>
        formatter.format(new Date(2023, i, 1))
      );
    },
    getDaysOfWeek() {
      const formatter = new Intl.DateTimeFormat(this.lang, {
        weekday: "short",
      });
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(2023, 0, i + 1); // Start from Sunday
        return formatter.format(date);
      });
    },
    formatDate(date, format) {
      const pad = (num) => String(num).padStart(2, "0");
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      let hours = date.getHours();
      const minutes = pad(date.getMinutes());
      let period = "";

      if (this.hourFormat === "12") {
        period = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
      }
      hours = pad(hours);

      return format
        .replace("yyyy", year)
        .replace("yy", String(year).slice(-2))
        .replace("mm", month)
        .replace("dd", day)
        .replace("HH", hours)
        .replace("hh", hours)
        .replace("MM", minutes)
        .replace("A", period);
    },
    formatDayAriaLabel(day) {
      const date = day.date;
      const month = this.monthNames[date.getMonth()];
      let label = `${day.day} ${month} ${date.getFullYear()}`;
      if (day.isInactive) {
        label += " (other month)";
      }
      if (this.isSelectedStart(date)) {
        label += " (range start)";
      } else if (this.isSelectedEnd(date)) {
        label += " (range end)";
      } else if (this.isInRange(date)) {
        label += " (in range)";
      }
      return label;
    },
    toggleCalendar() {
      if (!this.disabled && !this.inline) {
        this.showCalendar = !this.showCalendar;
        if (this.showCalendar) {
          const now = new Date();
          if (!this.selectedRange[0]) {
            this.selectedRange = [new Date(now), null];
            this.currentMonth = now.getMonth();
            this.currentYear = now.getFullYear();
            this.emitModelValue();
          }
          this.hours = now.getHours();
          this.minutes = now.getMinutes();
          this.updateTime();
          this.$nextTick(() => {
            this.focusSelectedDay();
          });
        }
      }
    },
    previousMonth() {
      this.currentMonth--;
      if (this.currentMonth < 0) {
        this.currentMonth = 11;
        this.currentYear--;
      }
      this.focusSelectedDay();
    },
    nextMonth() {
      if (this.isNextMonthDisabled) return;
      this.currentMonth++;
      if (this.currentMonth > 11) {
        this.currentMonth = 0;
        this.currentYear++;
      }
      this.focusSelectedDay();
    },
    selectDate(date) {
      if (this.isBeforeMinDate(date) || this.isAfterMaxDate(date)) return;
      const newDate = new Date(date);
      if (this.showTime) {
        newDate.setHours(this.hours || 0, this.minutes || 0);
      } else {
        newDate.setHours(0, 0, 0, 0);
      }

      if (this.selectionMode === "single") {
        this.selectedRange = [newDate, null];
        this.emitModelValue();
        if (!this.inline) {
          this.showCalendar = false;
        }
      } else {
        const [start, end] = this.selectedRange;
        if (!start || end) {
          // Start a new range
          this.selectedRange = [newDate, null];
        } else {
          // Complete the range
          if (newDate < start) {
            this.selectedRange = [newDate, start];
          } else {
            this.selectedRange = [start, newDate];
          }
        }
        this.emitModelValue();
        // Keep calendar open in range mode to allow further selections
      }

      this.$nextTick(() => {
        this.$refs.input?.focus();
      });
    },
    emitModelValue() {
      if (this.selectionMode === "single") {
        const date = this.selectedRange[0];
        this.$emit("update:modelValue", date ? date.toISOString() : null);
      } else {
        const [start, end] = this.selectedRange;
        this.$emit("update:modelValue", [
          start ? start.toISOString() : null,
          end ? end.toISOString() : null,
        ]);
      }
    },
    incrementHours() {
      this.hours = (this.hours + 1) % 24;
      this.updateTime();
    },
    decrementHours() {
      this.hours = (this.hours - 1 + 24) % 24;
      this.updateTime();
    },
    incrementMinutes() {
      this.minutes = (this.minutes + 1) % 60;
      this.updateTime();
    },
    decrementMinutes() {
      this.minutes = (this.minutes - 1 + 60) % 60;
      this.updateTime();
    },
    updateTime() {
      if (this.selectionMode === "single" && this.selectedRange[0]) {
        const newDate = new Date(this.selectedRange[0]);
        newDate.setHours(this.hours || 0, this.minutes || 0);
        this.selectedRange[0] = newDate;
        this.emitModelValue();
      } else if (this.selectionMode === "range") {
        const [start, end] = this.selectedRange;
        if (start) {
          const newStart = new Date(start);
          newStart.setHours(this.hours || 0, this.minutes || 0);
          this.selectedRange[0] = newStart;
        }
        if (end) {
          const newEnd = new Date(end);
          newEnd.setHours(this.hours || 0, this.minutes || 0);
          this.selectedRange[1] = newEnd;
        }
        this.emitModelValue();
      }
    },
    handleTimeKeydown(event, unit) {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (unit === "hours") this.incrementHours();
        else if (unit === "minutes") this.incrementMinutes();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        if (unit === "hours") this.decrementHours();
        else if (unit === "minutes") this.decrementMinutes();
      } else if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        if (unit === "hours") {
          this.$refs.minuteInput.focus();
        } else if (unit === "minutes" && this.showButtonBar) {
          this.$refs.clearButton?.focus() || this.$refs.todayButton.focus();
        }
      }
    },
    isSelectedStart(date) {
      if (!this.selectedRange[0]) return false;
      return date.toDateString() === this.selectedRange[0].toDateString();
    },
    isSelectedEnd(date) {
      if (!this.selectedRange[1]) return false;
      return date.toDateString() === this.selectedRange[1].toDateString();
    },
    isInRange(date) {
      if (!this.selectedRange[0] || !this.selectedRange[1]) return false;
      const start = new Date(this.selectedRange[0]);
      const end = new Date(this.selectedRange[1]);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      return date > start && date < end;
    },
    isToday(date) {
      return date.toDateString() === new Date().toDateString();
    },
    isBeforeMinDate(date) {
      if (!this.minDate) return false;
      const minDateCopy = new Date(this.minDate);
      minDateCopy.setHours(0, 0, 0, 0);
      return date < minDateCopy;
    },
    isAfterMaxDate(date) {
      if (!this.maxDate) return false;
      const maxDateCopy = new Date(this.maxDate);
      maxDateCopy.setHours(0, 0, 0, 0);
      return date > maxDateCopy;
    },
    clearDate() {
      this.selectedRange = [null, null];
      const now = new Date();
      this.hours = now.getHours();
      this.minutes = now.getMinutes();
      this.currentMonth = now.getMonth();
      this.currentYear = now.getFullYear();
      this.emitModelValue();
      if (!this.inline) {
        this.showCalendar = false;
      }
      this.$refs.input?.focus();
    },
    selectToday() {
      const now = new Date();
      const today = new Date(now);
      today.setHours(
        this.showTime ? this.hours || 0 : 0,
        this.showTime ? this.minutes || 0 : 0
      );
      if (this.selectionMode === "single") {
        this.selectedRange = [today, null];
      } else {
        this.selectedRange = [today, null];
      }
      this.currentMonth = today.getMonth();
      this.currentYear = today.getFullYear();
      this.emitModelValue();
      if (!this.inline) {
        this.showCalendar = false;
      }
      this.$refs.input?.focus();
    },
    handleClickOutside(event) {
      if (
        !this.inline &&
        this.$refs.calendarContainer &&
        !this.$refs.calendarContainer.contains(event.target)
      ) {
        this.showCalendar = false;
      }
    },
    handleInputKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleCalendar();
      }
    },
    handleIconKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleCalendar();
      }
    },
    handleDayKeydown(event, index) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.selectDate(this.calendarDays[index].date);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.focusDay(index + 1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.focusDay(index - 1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        this.focusDay(index + 7);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.focusDay(index - 7);
      } else if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        if (this.showTime) {
          this.$refs.hourInput.focus();
        } else if (this.showButtonBar) {
          this.$refs.clearButton?.focus() || this.$refs.todayButton.focus();
        }
      }
    },
    focusDay(index) {
      if (index >= 0 && index < this.calendarDays.length) {
        this.focusedDayIndex = index;
        const dayElement =
          this.$refs.calendarPanel.querySelectorAll(".day")[index];
        dayElement.focus();
      }
    },
    focusSelectedDay() {
      let indexToFocus = -1;
      if (this.selectionMode === "single" || !this.selectedRange[1]) {
        indexToFocus = this.calendarDays.findIndex((day) =>
          this.isSelectedStart(day.date)
        );
      } else {
        indexToFocus = this.calendarDays.findIndex((day) =>
          this.isSelectedEnd(day.date)
        );
      }
      if (indexToFocus === -1) {
        indexToFocus = this.calendarDays.findIndex((day) => !day.isInactive);
      }
      if (indexToFocus !== -1) {
        this.focusDay(indexToFocus);
      }
    },
  },
  watch: {
    showCalendar(newVal) {
      if (newVal) {
        const now = new Date();
        if (!this.selectedRange[0]) {
          this.selectedRange = [new Date(now), null];
          this.currentMonth = now.getMonth();
          this.currentYear = now.getFullYear();
          this.emitModelValue();
        }
        this.hours = now.getHours();
        this.minutes = now.getMinutes();
        this.updateTime();
        this.$nextTick(() => {
          this.focusSelectedDay();
        });
      }
    },
    modelValue(newVal) {
      if (this.selectionMode === "single") {
        if (newVal) {
          const newDate = new Date(newVal);
          this.selectedRange = [newDate, null];
          this.currentMonth = newDate.getMonth();
          this.currentYear = newDate.getFullYear();
          this.hours = newDate.getHours();
          this.minutes = newDate.getMinutes();
        } else {
          this.selectedRange = [null, null];
          const now = new Date();
          this.hours = now.getHours();
          this.minutes = now.getMinutes();
        }
      } else {
        if (Array.isArray(newVal)) {
          this.selectedRange = [
            newVal[0] ? new Date(newVal[0]) : null,
            newVal[1] ? new Date(newVal[1]) : null,
          ];
          const date = this.selectedRange[0] || this.selectedRange[1];
          if (date) {
            this.currentMonth = date.getMonth();
            this.currentYear = date.getFullYear();
            this.hours = date.getHours();
            this.minutes = date.getMinutes();
          }
        } else {
          this.selectedRange = [null, null];
          const now = new Date();
          this.hours = now.getHours();
          this.minutes = now.getMinutes();
        }
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    if (this.inline) {
      const now = new Date();
      if (!this.selectedRange[0]) {
        this.selectedRange = [new Date(now), null];
        this.currentMonth = now.getMonth();
        this.currentYear = now.getFullYear();
        this.hours = now.getHours();
        this.minutes = now.getMinutes();
        this.emitModelValue();
      }
      this.$nextTick(() => {
        this.focusSelectedDay();
      });
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.calendar-input-container {
  position: relative;
  width: 300px;
  font-family: "voyado Text", sans-serif;
}

.calendar-input-container.inline {
  width: auto;
}

.input-wrapper {
  position: relative;
}

.calendar-input {
  width: 100%;
  padding: 10px;
  padding-right: 40px;
  font-size: 16px;
  color: #21211f;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
}

.calendar-input:focus {
  outline: 2px solid #222222;
  border-style: double;
  box-shadow: none;
}

.calendar-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.p-invalid {
  border-color: #dc3545;
}

.p-error {
  color: #dc3545;
  font-size: 14px;
}

.calendar-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 16px;
}

.calendar-icon:focus {
  border-radius: 4px;
}

.calendar-container {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  border: 1px solid #c0c0bc;
  border-radius: 0.25rem;
  padding: 10px;
  background: white;
  z-index: 1006;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.calendar-input-container.inline .calendar-container {
  position: static;
  box-shadow: none;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  font-weight: 500;
}

.nav-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 5px 10px;
}

.nav-button:disabled {
  color: #c0c0bc;
  cursor: not-allowed;
}

.nav-button:focus {
  border-radius: 4px;
}

.month-year {
  font-size: 16px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.day-header {
  text-align: center;
  padding: 5px;
  font-weight: 500;
  font-size: 14px;
  color: #21211f;
}

.day {
  text-align: center;
  padding: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #21211f;
  border-radius: 4px;
}

.day:hover:not(.disabled):not(.selected-start):not(.selected-end) {
  background: #f0f0f0;
}

.day:focus {
  outline: 2px solid #222222;
}

.inactive {
  color: #666;
}

.selected-start,
.selected-end {
  background: #222222;
  color: white;
}

.range {
  background: #e0e0e0;
  color: #21211f;
}

.today {
  border: 1px solid #222222;
}

.disabled {
  color: #c0c0bc;
  cursor: not-allowed;
  background: #f5f5f5;
}

.time-picker {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
}

.time-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* border: 1px solid #c0c0bc; */
  /* border-radius: 4px; */
  background: white;
  /* padding: 2px; */
}

.time-arrow {
  background: none;
  border: none;
  cursor: pointer;
  /* padding: 4px 8px; */
  color: #666;
  line-height: 1;
}

/* .time-arrow:hover {
  color: #222222;
  background: #f0f0f0;
}

.time-arrow:focus {
  outline: 2px solid #222222;
  border-radius: 4px;
} */

.time-arrow i {
  font-size: 12px;
  display: inline-block;
}

.time-value {
  font-size: 16px;
  font-weight: 500;
  color: #21211f;
  padding: 4px 8px;
  text-align: center;
  min-width: 32px;
  display: inline-block;
  border-radius: 4px;
}

.time-value:focus {
  outline: 2px solid #222222;
  background: #f0f0f0;
}

.time-separator {
  font-size: 16px;
  font-weight: 500;
  color: #21211f;
  padding: 0 4px;
}

.button-bar {
  display: flex;
  justify-content: space-between;
  padding: 10px;
}

.clear-button,
.today-button {
  background: #f0f0f0;
  border: none;
  padding: 5px 10px;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 14px;
}

.clear-button:hover,
.today-button:hover {
  background: #e0e0e0;
}

.clear-button:focus,
.today-button:focus {
  outline: 2px solid #222222;
}
</style>